<script lang="ts" setup>
import { tpgIconSend } from '@tpg/grammar-web-icons';
import { ref } from 'vue';

import { GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import type { UserResultDTO } from '@nora-monorepo/nora-api-client';
import { formatName } from '@nora-monorepo/shared';
import {
  AvatarImage,
  borderSeparatorTransparency,
  getInitials,
  textPrimary,
  textTertiary,
  UserDetails,
} from '@nora-monorepo/ui-shared';
import type { GroupWithLocalRole } from '@nora-monorepo/ui-shared';

import { ConversationModalActionState } from '#nora-chat-ui/components/modals/new-conversation/conversation-modal-types';
import ConversationModal from '#nora-chat-ui/components/modals/new-conversation/ConversationModal.vue';
import { highlight as vHighlight } from '#nora-chat-ui/utils/highlight';

interface ItemProps {
  user: UserResultDTO;
  highlightText?: string;
  showStartConversationButton?: boolean;
}

const props = defineProps<ItemProps>();
const showCreateConversationModal = ref(false);
const initialUsers = ref<UserResultDTO[]>([]);
const initialGroups = ref<GroupWithLocalRole['group'][]>([]);
const userNameRef = ref();

async function toggleNewConversationModal() {
  initialUsers.value = [props.user];
  initialGroups.value = [];
  showCreateConversationModal.value = true;
}

function closeCreateConversationModal() {
  showCreateConversationModal.value = false;
  initialUsers.value = [];
  initialGroups.value = [];
}
</script>

<template>
  <ConversationModal
    v-if="showCreateConversationModal"
    :is-open="showCreateConversationModal"
    :mode="ConversationModalActionState.Create"
    :initial-users="initialUsers"
    :initial-groups="initialGroups"
    @close="closeCreateConversationModal"
  />
  <div ref="userNameRef" class="flex pr-2 w-full cursor-pointer">
    <div class="px-2 py-3">
      <AvatarImage :id="props.user.id" :text="getInitials(props.user)" />
    </div>
    <div
      class="flex items-center justify-between w-full overflow-hidden py-2 border-b"
      :class="borderSeparatorTransparency"
    >
      <div class="flex">
        <span
          v-highlight="{
            text: highlightText ?? '',
            class: `${textTertiary} font-bold`,
          }"
          class="font-semibold text-ellipsis overflow-hidden mr-1 whitespace-nowrap"
          :class="textPrimary"
        >
          {{ formatName(props.user) }}
        </span>
      </div>

      <div
        v-chat-can="{
          permission: 'create:chat.conversation',
        }"
      >
        <GrammarQuaternaryButton
          v-if="showStartConversationButton"
          :icon-template="tpgIconSend"
          @click="toggleNewConversationModal"
        />
      </div>
    </div>
    <UserDetails :trigger-element="userNameRef" :user="props.user" />
  </div>
</template>
